import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Switch,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import styled from "styled-components";
import { Colors } from "../Colors";
import { Agent, AgentType, EHR, Organization, QuestionAnswerPair } from "../types";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../store";
import { PrimaryButton } from "../subcomponents/CustomButton";
import { useSnackbar } from "../providers/SnackbarProvider";
import { updateAgentThunk } from "../slices/AgentSlice";
import { convertSnakeCaseToOfficial } from "../utils/utils";
import LoadingWithMessage from "../subcomponents/LoadingWithMessage";
import { addQuestionsAndAnswers, deleteQuestion, updateAnswer } from "../slices/QuestionAndAnswerSlice";
import Papa from "papaparse";
import { usePostHog } from "posthog-js/react";
import UploadCSVModal from "../components/modals/UploadCSVModal";
import stringSimilarity from "string-similarity-js";
import { setInboundRedirectNumber as setInboundRedirectNumberThunk, updateOrganizationAttribute } from "../slices/AuthSlice";
import { TimePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { HelpTooltip } from "../subcomponents/HelpTooltip";
import axios from "axios";
import { FooterBox, HeaderBox } from "../subcomponents/SidebarComponents";
import { InputStyles } from "../styles/GeneralStyles";

const StyledLink = styled.a`
  font-size: 0.8rem;
  margin-left: 10px;
  color: ${Colors.link};
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledTextField = styled(TextField)`
  width: 100%;
  background-color: ${Colors.textfield};
  border-radius: 10px;
  & .MuiInputBase-input {
    padding: 10px;
    font-size: 0.85rem;
    color: #ffffff;
  }
`;

interface SettingsTextInputProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  mb?: number; // TODO (Hizami): Probably not the best way to do this
  ml?: number;
  readOnly?: boolean;
}

const SettingsTextInput: React.FC<SettingsTextInputProps> = ({ label, value, onChange, mb, ml, readOnly }) => {
  return (
    <Typography
      variant="body1"
      fontSize={"1rem"}
      mb={mb}
      ml={ml}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      {label}:
      <TextField
        value={value}
        onChange={onChange}
        variant="outlined"
        InputProps={{
          readOnly: readOnly || false,
        }}
        sx={{
          marginLeft: "8px",
          "& .MuiInputBase-input": {
            color: "#FFF",
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
            background: Colors.textfield,
          },
          "& .MuiOutlinedInput-input": {
            padding: "6px 15px",
          },
        }}
      />
    </Typography>
  );
};

interface SettingsNumberInputProps {
  disabled: boolean;
  value: number;
  setter: (value: number) => void;
}

const SettingsNumberInput: React.FC<SettingsNumberInputProps> = ({ disabled, value, setter }) => {
  return (
    <TextField
      type="number"
      value={value}
      onChange={(e) => setter(parseInt(e.target.value))}
      variant="outlined"
      size="small"
      disabled={disabled}
      sx={{
        width: "45px",
        margin: "0 8px",
        "& .MuiInputBase-input": {
          color: "#FFF",
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: "8px",
          background: Colors.textfield,
        },
        "& .MuiOutlinedInput-input": {
          padding: "2px",
          textAlign: "center",
        },
        "& input[type=number]": {
          "-moz-appearance": "textfield", // Firefox
          "&::-webkit-outer-spin-button": {
            "-webkit-appearance": "none", // Chrome, Safari, Edge, Opera
            margin: 0,
          },
          "&::-webkit-inner-spin-button": {
            "-webkit-appearance": "none", // Chrome, Safari, Edge, Opera
            margin: 0,
          },
        },
      }}
    />
  );
};

interface DropdownProps {
  label: string;
  options: { key: string; value: string }[];
  selectedValue?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({ label, options, selectedValue, onChange, disabled }) => {
  const [selectedOption, setSelectedOption] = useState<string>(options.find((option) => option.key === selectedValue)?.key || "");

  const handleChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setSelectedOption(value);
    onChange(value);
  };

  return (
    <FormControl fullWidth variant="outlined" margin="normal">
      <InputLabel>{label}</InputLabel>
      <Select
        value={selectedOption}
        onChange={handleChange}
        label={label}
        disabled={disabled}
        sx={{
          "& .MuiInputBase-input": {
            padding: "10px",
            fontSize: "0.9rem",
          },
          "& input::placeholder": {
            fontSize: "0.9rem",
            padding: "10px",
          },
        }}
      >
        {options.map((option, index) => (
          <MenuItem key={index} value={option.key}>
            {option.value}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface ConfigureFormProps {
  agent: Agent;
  isModalOpen: boolean;
  onClose: () => void;
}

const ConfigureForm: React.FC<ConfigureFormProps> = ({ agent, isModalOpen, onClose }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const posthog = usePostHog();
  const organization: Organization | null = useSelector((state: RootState) => state.auth.organization);
  const questionsAndAnswers = useSelector((state: RootState) => state.questionsAndAnswers.questionsAndAnswers || []);

  const { agentType, name } = agent;

  const [agentName, setAgentName] = useState(name);
  const [callbackNumber, setCallbackNumber] = useState(organization?.phoneNumber);
  const [inboundRedirectNumber, setInboundRedirectNumber] = useState(organization?.inboundRedirectNumber);
  const [emergencyTransferNumber, setEmergencyTransferNumber] = useState(organization?.emergencyTransferNumber);

  const [maxDials, setMaxDials] = useState(agent.maxDials || 0);
  const [redialGapMinutes, setRedialGapMinutes] = useState(agent.redialGapMinutes);
  const [minutesToWaitBeforeCalling, setMinutesToWaitBeforeCalling] = useState(agent.minutesToWaitBeforeCalling || 0);
  const [minutesToCutoffBeforeAppointment, setMinutesToCutoffBeforeAppointment] = useState(agent.minutesToCutoffBeforeAppointment || 0);
  const [daysForwardToCheckAvailableSlots, setDaysForwardToCheckAvailableSlots] = useState(agent.daysForwardToCheckAvailableSlots || 0);
  const [minutesBetweenWaitlistGroups, setMinutesBetweenWaitlistGroups] = useState(agent.minutesBetweenWaitlistGroups || 0);
  const [minutesBetweenPatients, setMinutesBetweenPatients] = useState(agent.minutesBetweenPatients || 0);
  const [maxRunsPerPatient, setMaxRunsPerPatient] = useState(agent.maxRunsPerPatient || 100);
  const [maxRunsPerPatientPerDay, setMaxRunsPerPatientPerDay] = useState(agent.maxRunsPerPatientPerDay || 100);
  const [maxAppointmentsPerWeek, setMaxAppointmentsPerWeek] = useState(agent.maxAppointmentsPerWeek || 100);
  const [gapBetweenAppointments, setGapBetweenAppointments] = useState(agent.gapBetweenAppointments || 0);
  const [emailUpdatesTo, setEmailUpdatesTo] = useState(agent.emailUpdatesTo || "");

  const [enableTexting, setEnableTexting] = useState(agent.enableTexting);
  //const [enableAutomaticTextResponses, setEnableAutomaticTextResponses] = useState(agent.enableAutomaticTextResponses);
  const [enableCalling, setEnableCalling] = useState(agent.enableCalling);
  const [enableCancelAppointments, setEnableCancelAppointments] = useState(agent.enableCancelAppointments);
  const [randomPriority, setRandomPriority] = useState(agent.randomPriority || false);
  const [omitNoLongerAvailableText, setOmitNoLongerAvailableText] = useState(agent.omitNoLongerAvailableText);
  const [omitSameDayAppointments, setOmitSameDayAppointments] = useState(agent.omitSameDayAppointments);
  const [patientAdjustAvailability, setPatientAdjustAvailability] = useState(agent.patientAdjustAvailability);
  const [patientSelfSchedule, setPatientSelfSchedule] = useState(agent.patientSelfSchedule);
  const [ignoreApptType, setIgnoreApptType] = useState(agent.ignoreApptType);
  const [enableRedialing, setEnableRedialing] = useState(agent.enableRedialing);
  const [enableLeavingVoicemail, setEnableLeavingVoicemail] = useState(agent.enableLeavingVoicemail);
  //const [enableAutomaticCallScheduling, setEnableAutomaticCallScheduling] = useState(agent.enableAutomaticCallScheduling);
  const [isActive, setIsActive] = useState(agent.isActive);

  const [address, setAddress] = useState(agent.address || "");
  const [workingHours, setWorkingHours] = useState(agent.workingHours || "");

  const [autoStartWaitlistRuns, setAutoStartWaitlistRuns] = useState(agent.autoStartWaitlistRuns);
  const [reminders, setReminders] = useState(agent.reminders);
  const [reminderTime, setReminderTime] = useState(agent.reminderTime);

  const contactTypesMap: { [key: string]: string[] } = {};

  const [uploadingLoading, setUploadingLoading] = useState(false);
  const [loadingDefaultMsgs, setLoadingDefaultMsgs] = useState(false);

  const { showMessage } = useSnackbar();
  const dispatch = useAppDispatch();
  const [questionAnswerPairs, setQuestionAnswerPairs] = useState<QuestionAnswerPair[]>(questionsAndAnswers);
  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState("");
  const [newSource, setNewSource] = useState("");
  const [showSourceInput, setShowSourceInput] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [uploadCSVModalOpen, setUploadCSVModalOpen] = useState(false);

  const [workingHoursStart, setWorkingHoursStart] = useState(
    organization?.workingHoursStart ? DateTime.fromISO(organization.workingHoursStart) : null
  );
  const [workingHoursEnd, setWorkingHoursEnd] = useState(organization?.workingHoursEnd ? DateTime.fromISO(organization.workingHoursEnd) : null);

  const settingNumbersLoading = useSelector((state: RootState) => state.auth.settingPhoneNumbersLoading);

  const [intro, setIntro] = useState(agent.intro);
  const [welcomeText, setWelcomeText] = useState(agent.welcomeText);
  const [appointmentAvailableText, setAppointmentAvailableText] = useState(agent.appointmentAvailableText);
  const [appointmentBookedText, setAppointmentBookedText] = useState(agent.appointmentBookedText);
  const [appointmentTakenText, setAppointmentTakenText] = useState(agent.appointmentTakenText);
  const [initialExamText, setInitialExamText] = useState(agent.initialExamText);
  const [dischargedText, setDischargedText] = useState(agent.dischargedText);
  const [autoDischargedText, setAutoDischargedText] = useState(agent.autoDischargedText);
  const [planOfCareCreatedText, setPlanOfCareCreatedText] = useState(agent.planOfCareCreatedText);
  const [planOfCareUpdatedText, setPlanOfCareUpdatedText] = useState(agent.planOfCareUpdatedText);
  const [uploadCsvErrorMsg, setUploadCsvErrorMsg] = useState("");

  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const [duration, setDuration] = useState(agent.defaultAppointmentDuration || 30);

  useEffect(() => {
    if (gapBetweenAppointments > 2) {
      setGapBetweenAppointments(2);
    } else if (gapBetweenAppointments < 0) {
      setGapBetweenAppointments(0);
    }
  }, [gapBetweenAppointments]);

  useEffect(() => {
    const loadDefaultMessages = async () => {
      try {
        setLoadingDefaultMsgs(true);
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/prompts/default/${agent.agentId}`, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        });

        if (response.status !== 200) {
          return;
        }

        if (!intro) {
          setIntro(response.data.intro);
        }
      } catch (error) {
        console.error("Error loading default messages: ", error);
      } finally {
        setLoadingDefaultMsgs(false);
      }
    };

    loadDefaultMessages();
  }, []);

  const updatePhoneNumbers = async () => {
    if (!user?.token) {
      return;
    }
    setUploadingLoading(true);
    await dispatch(
      updateOrganizationAttribute({
        token: user.token,
        attribute: "phoneNumber",
        value: callbackNumber,
      })
    );
    await dispatch(
      updateOrganizationAttribute({
        token: user.token,
        attribute: "emergencyTransferNumber",
        value: emergencyTransferNumber,
      })
    );
    await dispatch(
      setInboundRedirectNumberThunk({
        token: user.token,
        inboundRedirectNumber: inboundRedirectNumber!,
      })
    );
    setUploadingLoading(false);
  };

  const updateWorkingHours = async () => {
    if (!user?.token) {
      return;
    }
    setUploadingLoading(true);
    if (workingHoursStart) {
      await dispatch(
        updateOrganizationAttribute({
          token: user.token,
          attribute: "workingHoursStart",
          value: workingHoursStart.toISO(),
        })
      );
    }
    if (workingHoursEnd) {
      await dispatch(
        updateOrganizationAttribute({
          token: user.token,
          attribute: "workingHoursEnd",
          value: workingHoursEnd.toISO(),
        })
      );
    }
    setUploadingLoading(false);
  };

  const handleSave = async () => {
    try {
      await updatePhoneNumbers();
      await updateWorkingHours();
      handleCloseModal();
    } catch (error) {
      console.error("Error updating phone numbers on Save: ", error);
    }
  };

  useEffect(() => {
    setWelcomeText(agent.welcomeText);
  }, [agent.welcomeText]);

  useEffect(() => {
    setAppointmentAvailableText(agent.appointmentAvailableText);
  }, [agent.appointmentAvailableText]);

  useEffect(() => {
    setAppointmentBookedText(agent.appointmentBookedText);
  }, [agent.appointmentBookedText]);

  useEffect(() => {
    setAppointmentTakenText(agent.appointmentTakenText);
  }, [agent.appointmentTakenText]);

  useEffect(() => {
    setInitialExamText(agent.initialExamText);
  }, [agent.initialExamText]);

  useEffect(() => {
    setDischargedText(agent.dischargedText);
  }, [agent.dischargedText]);

  useEffect(() => {
    setAutoDischargedText(agent.autoDischargedText);
  }, [agent.autoDischargedText]);

  useEffect(() => {
    setPlanOfCareCreatedText(agent.planOfCareCreatedText);
  }, [agent.planOfCareCreatedText]);

  useEffect(() => {
    setPlanOfCareUpdatedText(agent.planOfCareUpdatedText);
  }, [agent.planOfCareUpdatedText]);

  useEffect(() => {
    if (organization?.phoneNumber) {
      setCallbackNumber(organization.phoneNumber);
    }
  }, [organization?.phoneNumber]);

  useEffect(() => {
    if (organization?.inboundRedirectNumber) {
      setInboundRedirectNumber(organization.inboundRedirectNumber);
    }
  }, [organization?.inboundRedirectNumber]);

  useEffect(() => {
    if (organization?.emergencyTransferNumber) {
      setEmergencyTransferNumber(organization.emergencyTransferNumber);
    }
  }, [organization?.emergencyTransferNumber]);

  const handleFileChange = (file: File) => {
    if (file) {
      Papa.parse(file, {
        complete: function (results: any) {
          const headers = results.data[0];
          const dataRows = results.data.slice(1); // Exclude the header row

          // Ensure the CSV has the expected headers
          const expectedHeaders = ["question", "answer"];
          const hasValidHeaders = expectedHeaders.every((header) => headers.includes(header.toLowerCase()));

          if (!hasValidHeaders) {
            setUploadCsvErrorMsg("CSV file must have headers: 'question', 'answer'");
            return;
          }

          // Map the headers to their indices
          const headerIndexMap: { [key: string]: number } = headers.reduce((acc: any, header: string, index: number) => {
            acc[header] = index;
            return acc;
          }, {});

          let mostSimilarQuestion = "";
          let highestSimilarityRating = 0;

          // Convert each row into a QuestionAnswerPair and filter out close duplicates
          const questionsAndAnswersToAdd: QuestionAnswerPair[] = dataRows
            .map((row: any) => ({
              id: "", // Assuming id will be generated later or left empty
              question: row[headerIndexMap["question"]],
              answer: row[headerIndexMap["answer"]],
              source: row[headerIndexMap["source"]],
            }))
            .filter((newPair: QuestionAnswerPair) => {
              const newQuestion: string = newPair.question;

              const isDuplicate = questionAnswerPairs.some((pair) => {
                const similarityRating = stringSimilarity(newQuestion, pair.question);

                if (similarityRating > highestSimilarityRating) {
                  highestSimilarityRating = similarityRating;
                  mostSimilarQuestion = pair.question;
                }

                return similarityRating > 0.8;
              });

              return !isDuplicate;
            });

          if (questionsAndAnswersToAdd.length > 0) {
            // Dispatch the thunk with the filtered list of question-answer pairs
            if (!user?.token) {
              return;
            }
            dispatch(
              addQuestionsAndAnswers({
                token: user.token,
                questionsAndAnswers: questionsAndAnswersToAdd,
              })
            );
          } else {
            const errorMessage = `No new unique questions to add. Most similar existing question: "${mostSimilarQuestion}" that is ${(
              highestSimilarityRating * 100
            ).toFixed(0)}% similar.`;
            setErrorMessage(errorMessage);
          }
        },
        header: false,
      });
      setUploadCSVModalOpen(false);
      setUploadCsvErrorMsg("");
    }
  };

  useEffect(() => {
    setQuestionAnswerPairs(questionsAndAnswers);
  }, [questionsAndAnswers]);

  const handleCloseModal = async () => {
    const agentProps: Partial<Agent> = {
      agentId: agent.agentId,
      name: agentName,
      enableTexting,
      enableCalling,
      //enableAutomaticTextResponses,
      enableRedialing,
      maxDials,
      redialGapMinutes,
      enableCancelAppointments,
      autoStartWaitlistRuns,
      reminders,
      reminderTime,
      minutesToWaitBeforeCalling,
      minutesToCutoffBeforeAppointment,
      minutesBetweenWaitlistGroups,
      minutesBetweenPatients,
      randomPriority,
      maxRunsPerPatient,
      maxRunsPerPatientPerDay,
      maxAppointmentsPerWeek,
      gapBetweenAppointments,
      omitNoLongerAvailableText,
      omitSameDayAppointments,
      patientAdjustAvailability,
      patientSelfSchedule,
      daysForwardToCheckAvailableSlots,
      enableLeavingVoicemail,
      isActive,
      intro: intro,
      welcomeText,
      appointmentAvailableText,
      appointmentBookedText,
      appointmentTakenText,
      initialExamText,
      dischargedText,
      autoDischargedText,
      planOfCareCreatedText,
      planOfCareUpdatedText,
      emailUpdatesTo,
      ignoreApptType,
      defaultAppointmentDuration: duration,
      address,
      workingHours,
    };
    if (!user?.token) {
      showMessage("Session is expired. Please refresh.", "error");
      return;
    }
    dispatch(updateAgentThunk({ token: user.token, agent: agentProps }));
    onClose();
  };

  // useEffect(() => {
  //   if (!agent?.agentId) {
  //     return;
  //   }
  //   if (!agent?.namespaceId) {
  //     return;
  //   }
  //   const getQuestionsAndAnswersLocal = async () => {
  //     await dispatch(getQuestionsAndAnswers()); // expensive op $$$ -> avoid when possible!
  //   };
  //   getQuestionsAndAnswersLocal();
  // }, [agent?.agentId]);

  const handleEhrChange = (event: SelectChangeEvent<string>) => {
    const selectedEhr = event.target.value as EHR;
    if (!user?.token || !agent?.agentId) {
      return;
    }
    dispatch(
      updateAgentThunk({
        token: user.token,
        agent: { agentId: agent.agentId, ehr: selectedEhr },
      })
    );
  };

  const handleAddQuestionAnswer = () => {
    if (!user?.token) {
      return;
    }
    if (newQuestion && newAnswer) {
      let mostSimilarQuestion = "";
      let highestSimilarityRating = 0;

      // Check if the new question is too similar to any existing question
      const isDuplicate = questionAnswerPairs.some((pair) => {
        const similarityRating = stringSimilarity(newQuestion, pair.question);
        console.log(`Similarity rating for question "${newQuestion}" compared to "${pair.question}": ${similarityRating}`);

        if (similarityRating > highestSimilarityRating) {
          highestSimilarityRating = similarityRating;
          mostSimilarQuestion = pair.question;
        }

        return similarityRating > 0.8;
      });

      if (isDuplicate) {
        setErrorMessage(
          `Question "${newQuestion}" is too similar to existing question "${mostSimilarQuestion}", which is ${(highestSimilarityRating * 100).toFixed(
            0
          )}% similar.`
        );
        return;
      }

      // If not a duplicate, proceed to add the new question and answer
      const newPair: QuestionAnswerPair = {
        id: "",
        question: newQuestion,
        answer: newAnswer,
        source: newSource,
      };

      setQuestionAnswerPairs([...questionAnswerPairs, newPair]);
      setNewQuestion("");
      setNewAnswer("");
      setNewSource("");
      setShowSourceInput(false);
      dispatch(
        addQuestionsAndAnswers({
          token: user.token,
          questionsAndAnswers: [newPair],
        })
      );
    }
  };

  const handleUpdateQuestionAnswer = (index: number) => {
    if (!user?.token) {
      return;
    }
    const pair = questionAnswerPairs[index];
    if (pair) {
      dispatch(updateAnswer({ token: user.token, pair: pair }));
    }
  };

  const handleDeleteQuestionAnswer = (index: number) => {
    if (!user?.token) {
      return;
    }
    const pair = questionAnswerPairs[index];
    if (pair) {
      dispatch(deleteQuestion({ token: user.token, vectorId: pair.id }));
      setQuestionAnswerPairs(questionAnswerPairs.filter((_, i) => i !== index));
    }
  };

  const isFormValid = () => {
    return newQuestion.trim() !== "" && newAnswer.trim() !== "";
  };

  return (
    // <Modal open={isModalOpen} onClose={handleCloseModal} sx={{ overflowY: "auto" }}>
    <Box
      sx={{
        overflowY: "auto",
        height: "92%",
        paddingBottom: "40px",
        paddingRight: "20px",
      }}
    >
      <HeaderBox title="Configure" onClose={onClose} />

      <>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            marginTop: "20px",
          }}
        >
          <Typography variant="h4" sx={{ fontSize: "1.8rem" }}>
            {agentName}
          </Typography>
          <Switch
            checked={isActive}
            onChange={() => setIsActive(!isActive)}
            sx={{
              "& .MuiSwitch-switchBase": {
                color: "#FFFFFF",
                "& + .MuiSwitch-track": {
                  backgroundColor: "#CCCCCC",
                },
              },
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#FFFFFF",
                "& + .MuiSwitch-track": {
                  backgroundColor: "#00AC4F",
                },
              },
            }}
          />
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            Agent is <span style={{ fontWeight: "bold" }}>{isActive ? "on" : "off"}</span>
          </Box>
        </div>
        <Typography
          variant="h6"
          sx={{
            fontSize: "1.1rem",
            color: Colors.primary,
            textTransform: "capitalize",
            marginTop: "-5px",
          }}
        >
          {agentType}
        </Typography>

        <Box>
          <Box
            mt={3}
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ marginBottom: "20px" }}>
              <Typography variant="h6" mb={1}>
                Agent Info
              </Typography>

              <Box
                style={{
                  background: Colors.bg2,
                  padding: "15px",
                  borderRadius: "20px",
                }}
              >
                <Typography display={"flex"} justifyContent={"space-between"} mb={1.5}>
                  Agent ID: <span>{agent.agentId}</span>
                </Typography>

                <SettingsTextInput label="Agent name" mb={1} value={agentName} onChange={(e) => setAgentName(e.target.value)} />

                <Tooltip title="The phone number that will be read of to the user.">
                  <SettingsTextInput
                    label="Callback number"
                    mb={1}
                    value={callbackNumber || ""}
                    onChange={(e) => setCallbackNumber(e.target.value)}
                  />
                </Tooltip>

                <Tooltip title="This is the number that is redirected to if the patient calls the texting number back.">
                  <SettingsTextInput
                    label="Inbound redirect number"
                    mb={1}
                    value={inboundRedirectNumber || ""}
                    onChange={(e) => setInboundRedirectNumber(e.target.value)}
                  />
                </Tooltip>

                <Tooltip title="This is the number that is redirected to in the middle of a call if the call is going wrong or at the patient's request.">
                  <SettingsTextInput
                    label="Emergency transfer number"
                    mb={1}
                    value={emergencyTransferNumber || ""}
                    onChange={(e) => setEmergencyTransferNumber(e.target.value)}
                  />
                </Tooltip>

                <Typography variant="body1" style={{ margin: "10px 0 10px 0" }}>
                  Working hours
                </Typography>
                <Box display="flex" flexDirection="row" gap="20px">
                  <TimePicker
                    label="Start Time"
                    value={workingHoursStart}
                    onChange={setWorkingHoursStart}
                    disabled={settingNumbersLoading}
                    sx={InputStyles}
                  />
                  <TimePicker
                    label="End Time"
                    value={workingHoursEnd}
                    onChange={setWorkingHoursEnd}
                    disabled={settingNumbersLoading}
                    sx={InputStyles}
                  />
                </Box>

                {errorMessage && (
                  <Typography color="error" sx={{ marginTop: "5px", fontSize: "0.9rem" }}>
                    {errorMessage}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              style={{
                background: Colors.bg2,
                padding: "15px",
                borderRadius: "20px",
              }}
            >
              <Typography variant="body1" fontWeight={"bold"} fontSize={"1.1rem"} mb={1.5}>
                Agent Settings
              </Typography>

              <Tooltip title="Patient will not receive a text if the appointment is no longer available.">
                <Box display="flex" flexDirection="column" alignItems="flex-start" margin="0 0 5px -10px">
                  <Box display="flex" alignItems="center">
                    <Checkbox
                      checked={omitNoLongerAvailableText}
                      onClick={() => setOmitNoLongerAvailableText(!omitNoLongerAvailableText)}
                      sx={{ color: Colors.grey2 }}
                    />
                    <Typography style={{ fontSize: "0.9rem" }}>Omit no longer available text</Typography>
                  </Box>
                </Box>
              </Tooltip>

              {/*
              <Tooltip title="Whether or not the agent will try again on voicemail.">
                <Box display="flex" flexDirection="column" alignItems="flex-start" margin="0 0 5px -10px">
                  <Box display="flex" alignItems="center">
                    <Checkbox checked={enableRedialing} onClick={() => setEnableRedialing(!enableRedialing)} sx={{ color: Colors.grey2 }} />
                    <Typography style={{ fontSize: "0.9rem" }}>Enable redials</Typography>
                  </Box>

                  <Box display="flex" alignItems="center" marginTop="8px">
                    <Typography>
                      Max number of dials:
                      <SettingsNumberInput disabled={!enableRedialing} value={maxDials} setter={setMaxDials} />
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center" marginTop="8px">
                    <Typography>
                      Wait
                      <SettingsNumberInput disabled={!enableRedialing} value={redialGapMinutes} setter={setRedialGapMinutes} />
                      minutes before redialing
                    </Typography>
                  </Box>
                </Box>
              </Tooltip>
              */}

              {agentType === AgentType.waitlist && (
                <React.Fragment>
                  <Box display="flex" alignItems="center" margin="0 0 5px -10px">
                    <Checkbox
                      checked={autoStartWaitlistRuns}
                      onClick={() => setAutoStartWaitlistRuns(!autoStartWaitlistRuns)}
                      sx={{ color: Colors.grey2 }}
                    />
                    <Typography style={{ fontSize: "0.9rem" }}>Automatically start runs</Typography>
                  </Box>
                  {agent.ehr === EHR.webpt && (
                    <Box display="flex" alignItems="center" margin="0 0 5px -10px">
                      <Checkbox checked={reminders} onClick={() => setReminders(!reminders)} sx={{ color: Colors.grey2 }} />
                      <Typography style={{ fontSize: "0.9rem" }}>Send appointment reminders to patients</Typography>
                    </Box>
                  )}
                  {reminders && (
                    <TimePicker
                      label="Reminder time"
                      value={reminderTime ? DateTime.fromISO(reminderTime) : null}
                      onChange={(value) => setReminderTime(value?.toISO() || "")}
                      sx={{
                        ...InputStyles,
                        width: "200px",
                        marginLeft: "30px",
                        marginBottom: "20px",
                      }}
                    />
                  )}
                  <Typography mb={1}>
                    Expire runs
                    <SettingsNumberInput disabled={false} value={minutesToCutoffBeforeAppointment} setter={setMinutesToCutoffBeforeAppointment} />
                    minutes before appointment
                  </Typography>
                  <Typography mb={1}>
                    Check for availabilities
                    <SettingsNumberInput disabled={false} value={daysForwardToCheckAvailableSlots} setter={(v) => setDaysForwardToCheckAvailableSlots(Math.min(v, 30))} />
                    days forward
                  </Typography>

                  <hr />

                  <Typography variant="body1" fontWeight={"bold"} fontSize={"1rem"} mb={1.5}>
                    Additional Functionality
                  </Typography>

                  {agent.ehr === EHR.webpt && (
                    <Tooltip title="Agent can automatically pull upcoming patient appointments and cancel them if they are no longer available.">
                      <Box display="flex" flexDirection="column" alignItems="flex-start" margin="0 0 5px -10px">
                        <Box display="flex" alignItems="center">
                          <Checkbox
                            checked={enableCancelAppointments}
                            onClick={() => setEnableCancelAppointments(!enableCancelAppointments)}
                            sx={{ color: Colors.grey2 }}
                          />
                          <Typography style={{ fontSize: "0.9rem" }}>Allow agent to cancel appointments</Typography>
                        </Box>
                      </Box>
                    </Tooltip>
                  )}

                  <Tooltip title="Patient can adjust their availability directly by communicating with the agent.">
                    <Box display="flex" flexDirection="column" alignItems="flex-start" margin="0 0 5px -10px">
                      <Box display="flex" alignItems="center">
                        <Checkbox
                          checked={patientAdjustAvailability}
                          onClick={() => setPatientAdjustAvailability(!patientAdjustAvailability)}
                          sx={{ color: Colors.grey2 }}
                        />
                        <Typography style={{ fontSize: "0.9rem" }}>Patient can adjust availability</Typography>
                      </Box>
                    </Box>
                  </Tooltip>

                  <hr />

                  <Typography variant="body1" fontWeight={"bold"} fontSize={"1rem"} mb={1.5}>
                    Contact Delay
                  </Typography>
                  <Typography mb={1}>
                    Wait
                    <SettingsNumberInput disabled={false} value={minutesBetweenWaitlistGroups} setter={setMinutesBetweenWaitlistGroups} />
                    minutes until contacting the next priority group
                  </Typography>
                  <Typography mb={1}>
                    Wait
                    <SettingsNumberInput disabled={false} value={minutesBetweenPatients} setter={setMinutesBetweenPatients} />
                    minutes between each patient
                  </Typography>

                  <Tooltip title="Patients will be contacted in random order within each priority group.">
                    <Box display="flex" flexDirection="column" alignItems="flex-start" margin="0 0 5px -10px">
                      <Box display="flex" alignItems="center">
                        <Checkbox checked={randomPriority} onClick={() => setRandomPriority(!randomPriority)} sx={{ color: Colors.grey2 }} />
                        <Typography style={{ fontSize: "0.9rem" }}>Randomize patient priority within each group</Typography>
                      </Box>
                    </Box>
                  </Tooltip>

                  <hr />

                  <Typography variant="body1" fontWeight={"bold"} fontSize={"1rem"} mb={1.5}>
                    Patient Eligibility Criteria
                  </Typography>

                  {agent.ehr === EHR.webpt && (
                    <Tooltip title="Patient will not receive a text if they already have an appointment on the same day.">
                      <Box display="flex" flexDirection="column" alignItems="flex-start" margin="0 0 5px -10px">
                        <Box display="flex" alignItems="center">
                          <Checkbox
                            checked={omitSameDayAppointments}
                            onClick={() => setOmitSameDayAppointments(!omitSameDayAppointments)}
                            sx={{ color: Colors.grey2 }}
                          />
                          <Typography style={{ fontSize: "0.9rem" }}>Omit patients with same day appointments</Typography>
                        </Box>
                      </Box>
                    </Tooltip>
                  )}
                  <Typography>
                    Each patient can only have
                    <SettingsNumberInput disabled={false} value={maxRunsPerPatient} setter={setMaxRunsPerPatient} />
                    runs active per week
                  </Typography>
                  <Typography>
                    Each patient can only have
                    <SettingsNumberInput disabled={false} value={maxRunsPerPatientPerDay} setter={setMaxRunsPerPatientPerDay} />
                    runs active per day
                  </Typography>
                  {agent.ehr === EHR.webpt && (
                    <Typography>
                      Each patient can have a maximum of
                      <SettingsNumberInput disabled={false} value={maxAppointmentsPerWeek} setter={setMaxAppointmentsPerWeek} />
                      appointments booked per week
                    </Typography>
                  )}
                  {agent.ehr === EHR.webpt && (
                    <Typography>
                      The gap between appointments should be at least
                      <SettingsNumberInput disabled={false} value={gapBetweenAppointments} setter={setGapBetweenAppointments} />
                      days. (Max 2 days)
                    </Typography>
                  )}

                  <hr />

                  <Typography variant="body1" fontSize={"1rem"} mt={1} mb={1} ml={0}>
                    Welcome text:
                    <HelpTooltip
                      title={
                        <div>
                          This is welcome text that the agent will say to the patient when they get added to the waitlist.
                          <br />
                          <br />
                          You can use the following variables in the first sentence:
                          <br />
                          - &#123;&#123; agentName &#125;&#125; : The agent's name
                          <br />
                          - &#123;&#123; organizationName &#125;&#125;: The organization's name
                          <br />
                          - &#123;&#123; patientFirstName &#125;&#125;: The patient's first name
                          <br />
                          - &#123;&#123; patientLastName &#125;&#125;: The patient's last name
                          <br />
                          <br />
                          Make sure to include the brackets.
                          <br />
                          An example text could be: <br />
                          Hello &#123;&#123; patientFirstName &#125;&#125;, this is &#123;&#123; agentName &#125;&#125; from &#123;&#123;
                          organizationName &#125;&#125;. You've been added to the waitlist and may recieve text updates or calls about your status.
                        </div>
                      }
                    />
                    <TextField
                      multiline={true}
                      placeholder="Hey {{patientFirstName}}! This is {{agentName}}, an AI assistant from {{organizationName}}👋 You may receive text updates or calls about your schedule. Message and data rates may apply. To opt out, reply STOP. For help, reply HELP."
                      value={welcomeText}
                      onChange={(e) => setWelcomeText(e.target.value)}
                      variant="outlined"
                      sx={{
                        width: "100%",
                        marginLeft: "8px",
                        "& .MuiInputBase-input": {
                          color: "#FFF",
                        },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          background: Colors.textfield,
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "6px 15px",
                        },
                      }}
                    />
                  </Typography>
                  <Typography variant="body1" fontSize={"1rem"} mt={1} mb={1} ml={0}>
                    Appointment available text:
                    <HelpTooltip
                      title={
                        <div>
                          This is among the first sentences the agent will say to the patient.
                          <br />
                          <br />
                          You can use the following variables in the first sentence:
                          <br />
                          - &#123;&#123; agentName &#125;&#125; : The agent's name
                          <br />
                          - &#123;&#123; organizationName &#125;&#125;: The organization's name
                          <br />
                          - &#123;&#123; patientFirstName &#125;&#125;: The patient's first name
                          <br />
                          - &#123;&#123; patientLastName &#125;&#125;: The patient's last name
                          <br />
                          - &#123;&#123; appointmentDate &#125;&#125;: The appointment date and time
                          <br />
                          - &#123;&#123; pcpName &#125;&#125;: The primary care provider's name
                          <br />
                          - &#123;&#123; appointmentTypeName &#125;&#125;: The appointment type
                          <br />
                          <br />
                          Make sure to include the brackets.
                          <br />
                          An example first sentence could be: <br />
                          We have an availability for &#123;&#123; appointmentTypeName &#125;&#125; on &#123;&#123; appointmentDate &#125;&#125; with
                          &#123;&#123; pcpName &#125;&#125;. Would you like to take it?
                        </div>
                      }
                    />
                    <TextField
                      multiline={true}
                      placeholder="We have an availability for {{ appointmentTypeName }} on {{ appointmentDate }} with {{ pcpName }}. Would you like to take it?"
                      value={appointmentAvailableText}
                      onChange={(e) => setAppointmentAvailableText(e.target.value)}
                      variant="outlined"
                      sx={{
                        width: "100%",
                        marginLeft: "8px",
                        "& .MuiInputBase-input": {
                          color: "#FFF",
                        },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          background: Colors.textfield,
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "6px 15px",
                        },
                      }}
                    />
                  </Typography>
                  <Typography variant="body1" fontSize={"1rem"} mt={1} mb={1} ml={0}>
                    Appointment booked text:
                    <HelpTooltip
                      title={
                        <div>
                          This is the text the agent will say to the patient when they have successfully booked an appointment from the waitlist.
                          <br />
                          <br />
                          You can use the following variables in the first sentence:
                          <br />
                          - &#123;&#123; patientFirstName &#125;&#125;: The patient's first name
                          <br />
                          - &#123;&#123; patientLastName &#125;&#125;: The patient's last name
                          <br />
                          - &#123;&#123; appointmentDate &#125;&#125;: The appointment date and time
                          <br />
                          - &#123;&#123; pcpName &#125;&#125;: The primary care provider's name
                          <br />
                          - &#123;&#123; appointmentTypeName &#125;&#125;: The appointment type
                          <br />
                          <br />
                          Make sure to include the brackets.
                          <br />
                          An example response could be: <br />
                          Awesome! That time is all yours and you're officially booked for &#123;&#123; appointmentDate &#125;&#125; with Dr.
                          &#123;&#123; pcpName &#125;&#125;. Look forward to seeing you then, &#123;&#123; patientFirstName &#125;&#125;!
                        </div>
                      }
                    />
                    <TextField
                      multiline={true}
                      placeholder="Your appointment on {{ appointmentDate }} has been successfully booked! Thank you!"
                      value={appointmentBookedText}
                      onChange={(e) => setAppointmentBookedText(e.target.value)}
                      variant="outlined"
                      sx={{
                        width: "100%",
                        marginLeft: "8px",
                        "& .MuiInputBase-input": {
                          color: "#FFF",
                        },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          background: Colors.textfield,
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "6px 15px",
                        },
                      }}
                    />
                  </Typography>
                  <Typography variant="body1" fontSize={"1rem"} mt={1} mb={1} ml={0}>
                    Appointment taken text:
                    <HelpTooltip
                      title={
                        <div>
                          This is the text the agent will say to the patient when an appointment from the waitlist is taken by another patient.
                          <br />
                          <br />
                          You can use the following variables in the first sentence:
                          <br />
                          - &#123;&#123; patientFirstName &#125;&#125;: The patient's first name
                          <br />
                          - &#123;&#123; patientLastName &#125;&#125;: The patient's last name
                          <br />
                          - &#123;&#123; appointmentDate &#125;&#125;: The appointment date and time
                          <br />
                          - &#123;&#123; pcpName &#125;&#125;: The primary care provider's name
                          <br />
                          - &#123;&#123; appointmentTypeName &#125;&#125;: The appointment type
                          <br />
                          <br />
                          Make sure to include the brackets.
                          <br />
                          An example response could be: <br />
                          I'm sorry, but the appointment on &#123;&#123; appointmentDate &#125;&#125; with Dr. &#123;&#123; pcpName &#125;&#125; has
                          been taken by another patient.
                        </div>
                      }
                    />
                    <TextField
                      multiline={true}
                      placeholder="I'm sorry, but the appointment on {{ appointmentDate }} has been taken by another patient and is no longer available."
                      value={appointmentTakenText}
                      onChange={(e) => setAppointmentTakenText(e.target.value)}
                      variant="outlined"
                      sx={{
                        width: "100%",
                        marginLeft: "8px",
                        "& .MuiInputBase-input": {
                          color: "#FFF",
                        },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          background: Colors.textfield,
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "6px 15px",
                        },
                      }}
                    />
                  </Typography>
                  {agent.ehr === EHR.webpt && (
                    <Typography variant="body1" fontSize={"1rem"} mt={1} mb={1} ml={0}>
                      Initial examination text:
                      <HelpTooltip
                        title={
                          <div>
                            This is the text the agent will say to the patient to book an initial examination.
                            <br />
                            <br />
                            You can use the following variables in the text:
                            <br />
                            - &#123;&#123; patientFirstName &#125;&#125;: The patient's first name
                            <br />
                            - &#123;&#123; patientLastName &#125;&#125;: The patient's last name
                            <br />
                            - &#123;&#123; link &#125;&#125;: The link for the patient to book plan of care
                            <br />
                            - &#123;&#123; clinicPhoneNumber &#125;&#125;: The clinic's phone number
                            <br />
                            <br />
                            Make sure to include the brackets.
                            <br />
                            An example response could be: <br />
                            Hey &#123;&#123; patientFirstName &#125;, ready to schedule your initial examination? You can book your visit by following
                            this link: &#123;&#123; link &#125;&#125;. This link will EXPIRE IN 24 HOURS. If you need further assistance, please call
                            or text the clinic at &#123;&#123; clinicPhoneNumber &#125;&#125;
                          </div>
                        }
                      />
                      <TextField
                        multiline={true}
                        placeholder="Hey {{ patientFirstName }}, ready to book your first visit? To schedule visit: {{ link }}. This link will EXPIRE IN 24 HOURS."
                        value={initialExamText}
                        onChange={(e) => setInitialExamText(e.target.value)}
                        variant="outlined"
                        sx={{
                          width: "100%",
                          marginLeft: "8px",
                          "& .MuiInputBase-input": {
                            color: "#FFF",
                          },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            background: Colors.textfield,
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "6px 15px",
                          },
                        }}
                      />
                    </Typography>
                  )}
                  {agent.ehr === EHR.webpt && (
                    <Typography variant="body1" fontSize={"1rem"} mt={1} mb={1} ml={0}>
                      Plan of care created text:
                      <HelpTooltip
                        title={
                          <div>
                            This is the text the agent will say to the patient when a patient has been assigned a new plan of care in penciled.
                            <br />
                            <br />
                            You can use the following variables in the text:
                            <br />
                            - &#123;&#123; patientFirstName &#125;&#125;: The patient's first name
                            <br />
                            - &#123;&#123; patientLastName &#125;&#125;: The patient's last name
                            <br />
                            - &#123;&#123; link &#125;&#125;: The link for the patient to book plan of care
                            <br />
                            - &#123;&#123; clinicPhoneNumber &#125;&#125;: The clinic's phone number
                            <br />
                            <br />
                            Make sure to include the brackets.
                            <br />
                            An example response could be: <br />
                            Hey &#123;&#123; patientFirstName &#125;, You have been assigned a new plan of care. You can book your visits by following
                            this link: &#123;&#123; link &#125;&#125;. This link will EXPIRE IN 24 HOURS. If you need further assistance, please call
                            or text the clinic at &#123;&#123; clinicPhoneNumber &#125;&#125;
                          </div>
                        }
                      />
                      <TextField
                        multiline={true}
                        placeholder="Hey {{ patientFirstName }}, your therapist would like you to schedule appointments. To schedule visit: {{ link }}. This link will EXPIRE IN 24 HOURS. You've been automatically added to the waitlist for any visits that you do not book in your plan. To update your waitlist availability, just ask."
                        value={planOfCareCreatedText}
                        onChange={(e) => setPlanOfCareCreatedText(e.target.value)}
                        variant="outlined"
                        sx={{
                          width: "100%",
                          marginLeft: "8px",
                          "& .MuiInputBase-input": {
                            color: "#FFF",
                          },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            background: Colors.textfield,
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "6px 15px",
                          },
                        }}
                      />
                    </Typography>
                  )}
                  {agent.ehr === EHR.webpt && (
                    <Typography variant="body1" fontSize={"1rem"} mt={1} mb={1} ml={0}>
                      Plan of care updated text:
                      <HelpTooltip
                        title={
                          <div>
                            This is the text the agent will say to the patient when a patient's plan of care is modified in penciled.
                            <br />
                            <br />
                            You can use the following variables in the text:
                            <br />
                            - &#123;&#123; patientFirstName &#125;&#125;: The patient's first name
                            <br />
                            - &#123;&#123; patientLastName &#125;&#125;: The patient's last name
                            <br />
                            - &#123;&#123; link &#125;&#125;: The link for the patient to book plan of care
                            <br />
                            - &#123;&#123; clinicPhoneNumber &#125;&#125;: The clinic's phone number
                            <br />
                            <br />
                            Make sure to include the brackets.
                            <br />
                            An example response could be: <br />
                            Hey &#123;&#123; patientFirstName &#125;, Your plan of care has been modified. You can book your visits by following this
                            link: &#123;&#123; link &#125;&#125;. This link will EXPIRE IN 24 HOURS. If you need further assistance, please call or
                            text the clinic at &#123;&#123; clinicPhoneNumber &#125;&#125;
                          </div>
                        }
                      />
                      <TextField
                        multiline={true}
                        placeholder="Hey {{ patientFirstName }}, your therapist would like you to schedule more appointments. To schedule visit: {{ link }}. This link will EXPIRE IN 24 HOURS. You've been automatically added to the waitlist for any visits that you do not book in your plan. To update your waitlist availability, just ask."
                        value={planOfCareUpdatedText}
                        onChange={(e) => setPlanOfCareUpdatedText(e.target.value)}
                        variant="outlined"
                        sx={{
                          width: "100%",
                          marginLeft: "8px",
                          "& .MuiInputBase-input": {
                            color: "#FFF",
                          },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            background: Colors.textfield,
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "6px 15px",
                          },
                        }}
                      />
                    </Typography>
                  )}
                  {agent.ehr === EHR.webpt && (
                    <Typography variant="body1" fontSize={"1rem"} mt={1} mb={1} ml={0}>
                      Discharged text:
                      <HelpTooltip
                        title={
                          <div>
                            This is the text the agent will say to a patient who is offered a waitlist spot but is already discharged/archived in Penciled.
                            <br />
                            <br />
                            You can use the following variables in the text:
                            <br />
                            - &#123;&#123; patientFirstName &#125;&#125;: The patient's first name
                            <br />
                            - &#123;&#123; patientLastName &#125;&#125;: The patient's last name
                            <br />
                            - &#123;&#123; clinicPhoneNumber &#125;&#125;: The clinic's phone number
                            <br />
                            <br />
                            Make sure to include the brackets.
                            <br />
                            An example response could be: <br />
                            I'm sorry, my records indicate that you are no longer an active patient at this clinic. Please contact the clinic at
                            &#123;&#123; clinicPhoneNumber &#125;&#125; for help.
                          </div>
                        }
                      />
                      <TextField
                        multiline={true}
                        placeholder="I'm sorry, my records indicate that you are no longer an active patient at this clinic. Please contact the clinic at {{ clinicPhoneNumber }} for help."
                        value={dischargedText}
                        onChange={(e) => setDischargedText(e.target.value)}
                        variant="outlined"
                        sx={{
                          width: "100%",
                          marginLeft: "8px",
                          "& .MuiInputBase-input": {
                            color: "#FFF",
                          },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            background: Colors.textfield,
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "6px 15px",
                          },
                        }}
                      />
                    </Typography>
                  )}
                  {agent.ehr === EHR.webpt && (
                    <Typography variant="body1" fontSize={"1rem"} mt={1} mb={1} ml={0}>
                      Auto-discharged text:
                      <HelpTooltip
                        title={
                          <div>
                            This is the text sent to the patient when their case is automatically discharged.
                            <br />
                            <br />
                            You can use the following variables in the text:
                            <br />
                            - &#123;&#123; patientFirstName &#125;&#125;: The patient's first name
                            <br />
                            - &#123;&#123; patientLastName &#125;&#125;: The patient's last name
                            <br />
                            - &#123;&#123; organizationName &#125;&#125;: The organization's name
                            <br />
                            - &#123;&#123; clinicPhoneNumber &#125;&#125;: The clinic's phone number
                            <br />
                            <br />
                            Make sure to include the brackets.
                            <br />
                            An example response could be: <br />
                            Hi &#123;&#123; patientFirstName &#125;&#125;, this is &#123;&#123; organizationName &#125;&#125;. Your case has been
                            discharged. If you have any questions, feel free to reach out to us at &#123;&#123; clinicPhoneNumber &#125;&#125;. Thank
                            you for choosing &#123;&#123; organizationName &#125;&#125;!
                          </div>
                        }
                      />
                      <TextField
                        multiline={true}
                        placeholder="Hi {{ patientFirstName }}, this is {{ organizationName }}. Your case has been discharged. If you have any questions, feel free to reach out to us at {{ clinicPhoneNumber }}. Thank you for choosing {{ organizationName }}!"
                        value={autoDischargedText}
                        onChange={(e) => setAutoDischargedText(e.target.value)}
                        variant="outlined"
                        sx={{
                          width: "100%",
                          marginLeft: "8px",
                          "& .MuiInputBase-input": {
                            color: "#FFF",
                          },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            background: Colors.textfield,
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "6px 15px",
                          },
                        }}
                      />
                    </Typography>
                  )}
                </React.Fragment>
              )}
            </Box>

            <Typography variant="h6" mt={2}>
              Patient Add Settings
            </Typography>

            <Box
              style={{
                background: Colors.bg2,
                padding: "15px",
                borderRadius: "20px",
              }}
            >
              <FormControl fullWidth>
                <Tooltip title="This field is required">
                  <Typography variant="body1" mt={1} mb={1}>
                    Default appointment duration (minutes) <span style={{ color: Colors.error }}>*</span>
                  </Typography>
                </Tooltip>
                <TextField
                  variant="outlined"
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  placeholder="Enter duration"
                  fullWidth
                  sx={{
                    borderRadius: "10px",
                    background: Colors.textfield,
                    "& .MuiInputBase-input": {
                      padding: "15px",
                      fontSize: "0.9rem",
                      color: "#FFFFFF",
                    },
                  }}
                />
              </FormControl>
            </Box>

            <Typography variant="h6" mt={2}>
              Communication settings
            </Typography>

            <Box display="flex" alignItems="center" margin="0 0 5px -10px">
              <Checkbox checked={enableCalling} onClick={() => setEnableCalling(!enableCalling)} sx={{ color: Colors.grey2 }} />
              <Typography style={{ fontSize: "0.9rem" }}>Phone call</Typography>
            </Box>

            <Box
              style={{
                background: Colors.bg2,
                padding: "15px",
                borderRadius: "20px",
              }}
            >
              <Typography variant="body1" fontWeight={"bold"} fontSize={"1.1rem"} mb={1.5}>
                Phone call settings{" "}
              </Typography>

              <Typography mb={1.5} variant="body1">
                Phone number: <strong>{agent.phoneNumber}</strong>
                <StyledLink
                  href="https://calendly.com/penciled/shawn"
                  target="_blank"
                  style={{
                    fontSize: "0.8rem",
                    marginLeft: "10px",
                    color: Colors.link,
                    cursor: "pointer",
                  }}
                >
                  Request new phone number
                </StyledLink>
              </Typography>

              <Typography mb={1.5} variant="body1">
                Spam risk prevention: <strong style={{ color: "#00AC4F", marginLeft: "15px" }}>Complete</strong>
              </Typography>

              {enableCalling && enableTexting && (
                <Typography>
                  Start calling
                  <SettingsNumberInput
                    disabled={!enableTexting || !enableCalling}
                    value={minutesToWaitBeforeCalling}
                    setter={setMinutesToWaitBeforeCalling}
                  />
                  minutes after sending texts
                </Typography>
              )}
            </Box>

            {agent.agentType === AgentType.waitlist && (
              <Box>
                <Box display="flex" alignItems="center" margin="15px 0 5px -10px">
                  <Checkbox checked={enableTexting} onClick={() => setEnableTexting(!enableTexting)} sx={{ color: Colors.grey2 }} />
                  <Typography fontSize={"0.9rem"}>Text message</Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ marginTop: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: "1.25rem",
                fontWeight: 600,
                marginBottom: 2,
              }}
            >
              Clinic Information
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: Colors.grey1,
                marginBottom: 3,
              }}
            >
              Provide answers to common questions about your clinic. This information will be used by the agent when speaking with patients.
            </Typography>

            {/* Address Input */}
            <Box
              sx={{
                background: Colors.bg2,
                padding: "20px",
                borderRadius: "12px",
                marginBottom: "16px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 2,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: Colors.white,
                    flex: 1,
                    marginRight: 2,
                  }}
                >
                  What is your clinic's address?
                </Typography>

                <TextField
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  multiline
                  rows={3}
                  sx={{
                    flex: 2,
                    "& .MuiInputBase-root": {
                      background: Colors.textfield,
                      borderRadius: "8px",
                      color: Colors.white,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: Colors.grey1,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: Colors.primary,
                    },
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: Colors.grey2 }}>
                This address will be provided to patients when they ask for the location.
              </Typography>
            </Box>

            {/* Working Hours Input */}
            <Box
              sx={{
                background: Colors.bg2,
                padding: "20px",
                borderRadius: "12px",
                marginBottom: "16px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 2,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: Colors.white,
                    flex: 1,
                    marginRight: 2,
                  }}
                >
                  What are your clinic's working hours?
                </Typography>

                <TextField
                  value={workingHours}
                  onChange={(e) => setWorkingHours(e.target.value)}
                  multiline
                  rows={4}
                  placeholder="Monday-Friday: 9:00 AM - 5:00 PM&#13;&#10;Saturday: 10:00 AM - 2:00 PM&#13;&#10;Sunday: Closed"
                  sx={{
                    flex: 2,
                    "& .MuiInputBase-root": {
                      background: Colors.textfield,
                      borderRadius: "8px",
                      color: Colors.white,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: Colors.grey1,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: Colors.primary,
                    },
                  }}
                />
              </Box>

              <Typography variant="caption" sx={{ color: Colors.grey2 }}>
                Enter your clinic's hours in a format that's easy for patients to understand. Include different hours for different days if
                applicable.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginTop: { xs: "15px", sm: "5px" },
              fontSize: { xs: "0.9rem", sm: "1rem" },
            }}
          >
            <Typography variant="h6" mb={1} mt={3}>
              Integrations
            </Typography>

            <Box
              mt={2}
              style={{
                background: Colors.bg2,
                padding: "15px",
                borderRadius: "20px",
              }}
            >
              <Typography variant="body1" fontWeight={"bold"} fontSize={"1.1rem"} mb={2}>
                Integration settings
              </Typography>

              <Box display="flex" alignItems="center" justifyContent={"space-between"} marginBottom={1}>
                <Typography fontSize={"0.9rem"} mr={1}>
                  EHR:
                </Typography>
                <FormControl
                  variant="outlined"
                  size="small"
                  sx={{
                    width: "100%",
                    background: Colors.textfield,
                    borderRadius: "10px",
                    "& .MuiInputBase-input": {
                      color: "#FFF",
                    },
                  }}
                >
                  <InputLabel style={{ color: Colors.grey1 }}>Select an option</InputLabel>
                  <Select value={agent?.ehr || EHR.none} onChange={handleEhrChange} label="Select an option">
                    {Object.values(EHR).map((ehr) => (
                      <MenuItem key={ehr} value={ehr}>
                        {convertSnakeCaseToOfficial(ehr)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* {(uploadingLoading || settingNumbersLoading) && (
          <LoadingWithMessage message="Updating phone numbers..." size={10} customStyles={{ fontSize: "1.2rem" }} />
        )} */}

        <FooterBox onClick={handleSave} loading={uploadingLoading || settingNumbersLoading} buttonText="Save" />
      </>
    </Box>
    // </Modal>
  );
};

export default ConfigureForm;
